import { Component, Input, OnChanges, OnInit, SimpleChanges } from "@angular/core";
import { jsPDF } from "jspdf";
import { AppMainComponent } from "src/app/app.main.component";
import { ITermsConditions } from "src/app/configurations/inventory/orders/models/terms-conditions";
import { OrdersService } from "src/app/configurations/inventory/orders/orders.service";
import { Observable, Subject, catchError, forkJoin, lastValueFrom, of, takeUntil } from "rxjs";
import { AppMainService } from "src/app/app-main.service";
import { ImportserialService } from "src/app/modules/shared/services/importserial.service";
import { ConfigurationService } from "src/app/configurations/configurationService";
import { CustomAttributeFieldType, PrintType, SOChannel, SoStageId, TaxCountry } from "src/app/inventory/sales-order/constants/constants";
import { UtilityService } from "src/app/inventory/sales-order/utils/utility.service";
import { RobotoFont } from "src/app/inventory/sales-order/utils/vfs";
import { SalesOrderService } from "src/app/inventory/sales-order/sales-order.service";
import { TaxSplitService } from "../products-tax-split-view/service/tax-split.service";

@Component({
    selector: "app-so-pdf",
    templateUrl: "./so-pdf.component.html",
    styleUrls: ["./so-pdf.component.scss"],
})
export class SoPdfComponent implements OnChanges, OnInit {
    @Input() channel;
    @Input() objectName;
    @Input() detailsData: any;
    @Input() purchaseListTB = [];
    @Input() template: any;
    @Input() customFields: any;
    @Input() termsConditions: ITermsConditions;

    protected ngUnsubscribe$: Subject<void> = new Subject<void>();

    utilService: UtilityService;
    fieldTypes = CustomAttributeFieldType;
    productsSerialDetails = [];
    soChannel = SOChannel;
    billingAddress: any;
    shippingAddress: any;
    billingAddressContact: any;
    shippingAddressContact: any;
    docName = "";
    trackingOptions: any[] = [];

    // barcode properties
    elementType = "img";
    format = "CODE128";
    lineColor = "#000000";
    width = 1;
    height = 20;
    displayValue = true;
    fontOptions = "";
    font = "monospace";
    textAlign = "center";
    textPosition = "bottom";
    textMargin = 5;
    fontSize = 14;
    background = "#ffffff";
    margin = 10;
    marginTop = 10;
    marginBottom = 10;
    marginLeft = 0;
    marginRight = 10;
    bundleLists: any[] = [];
    signature;
    imagePath: any;
    appLogoPath: any;

    soStage = SoStageId;
    generatedPdfFileName = {
        [SOChannel.ESTIMATES]: "Estimate",
        [SOChannel.INVOICING]: "Invoice",
        [SOChannel.SALES_ORDER]: "SalesOrder",
        [SOChannel.CREDIT_MEMO]: "CreditMemo",
        [PrintType.PICKSLIPPRINT]: "PickSlip",
        [PrintType.PACKSLIPPRINT]: "PackSlip",
    };
    pdfHeader = {
        [SOChannel.ESTIMATES]: "Estimate",
        [SOChannel.INVOICING]: "Invoice",
        [SOChannel.SALES_ORDER]: "Sales Order",
        [SOChannel.CREDIT_MEMO]: "Credit Memo",
    };
    totalAmount: number = 0;
    productsTotalAdditionalAmount: number = 0;
    orgHeadOfficeAddress;
    companyInfo;

    showTaxBasedSplitView = false;
    isStateMatched = false;
    taxBasedProductsList = [];
    taxBasedProductsCols = [];

    // fraction digits
    fractionDigits: number = 0;
    qtyFractionDigits: number = 0;

    // currency
    savedCurrencyUnit: any;
    savedCurrencyExchangeRate: any = 1;
    baseCurrency: any;
    orgCurrency = [];

    constructor(
        private utilityService: UtilityService,
        public app: AppMainComponent,
        private ordersService: OrdersService,
        private appMainService: AppMainService,
        private importService: ImportserialService,
        private configurationService: ConfigurationService,
        private salesOrderService: SalesOrderService,
        private taxSplitService: TaxSplitService,
    ) {
        this.utilService = this.utilityService;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.detailsData && changes.detailsData.currentValue) {
            this.detailsData = changes.detailsData.currentValue;
            this.setAddressData();
            this.calculateTotalAmount();

        }
        if (changes.channel && changes.channel.currentValue) {
            this.channel = changes.channel.currentValue;
            this.setDocName();
        }
        if (changes.purchaseListTB && changes.purchaseListTB.currentValue) {
            this.purchaseListTB = changes.purchaseListTB.currentValue;
            this.getBundleLength();
        }
        if (changes.objectName && changes.objectName.currentValue) {
            this.objectName = changes.objectName.currentValue;
        }
        this.getProductSerialDetails();
    }

    ngOnInit(): void {
        this.getFractionDigits();
        this.getQtyDecimalDigits();
        this.getBaseCurrency();
        this.getOrganizationBranchesByOrgId();
        this.getTrackingOptions();
    }
    getFractionDigits() {
        this.importService
            .getDecimalValue()
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: (value) => {
                    if (value) {
                        this.fractionDigits = value;
                    }
                },
                error: (error) => {},
            });
    }
    getQtyDecimalDigits() {
        this.importService.getQtyDecimalValue().subscribe((value) => {
            if (value) {
                this.qtyFractionDigits = value;
            }
        });
    }
    getBaseCurrency() {
        this.importService
            .getOrgCurrency()
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe((value) => {
                if (value) {
                    this.baseCurrency = value.find((cr) => cr.isBaseCurrency);
                    this.orgCurrency = value;
                }
            });
    }

    getTrackingOptions() {
        this.salesOrderService
            .loadTrackingOptions()
            .pipe()
            .subscribe((options) => {
                this.trackingOptions = options;
            });
    }
    async getOrganizationBranchesByOrgId() {
        const org = await lastValueFrom(this.configurationService.getOrganizationBranchesByOrgId(this.app.user.organizationId));
        if (org && org.length) {
            this.orgHeadOfficeAddress = org.find((o) => o?.type?.name?.toLowerCase() == "head office");
            this.prepareCompanyInfoToPrint(org);
        }
    }
    prepareCompanyInfoToPrint(branches) {
        let newBranches = [];
        for (let b of branches) {
            let branch = {
                id: b.id,
                name: b.address.name == "" ? "" : b.address.name,
                phone: b.organizationAddressPhone && b.organizationAddressPhone.length ? b.organizationAddressPhone[0].phone.number : "",
                emailId: b.organizationAddressEmail && b.organizationAddressEmail.length ? b.organizationAddressEmail[0].email.emailAddress : "",
                addressId: b.addressId,
                address: b.address.streetAddress1 + (b.address.streetAddress2 ? " " + b.address.streetAddress2 : ""),
                city: b.address.city == null ? "" : b.address.city.name,
                cityId: b.address.cityId,
                cityObj: null,
                stateId: b.address.stateId,
                state: b?.address?.state?.name ?? "",
                stateObj: null,
                countryId: b.address?.countryId,
                country: b.address?.country?.name,
                countryObj: null,
                zip: b.address.zipCode,
                locationTypeId: b.locationTypeId,
                locationType: b.locationType ? b.locationType.name : 0,
                headOffice: b.typeId == this.orgHeadOfficeAddress?.id ? true : false,
                latitude: b.address.latitude,
                longitude: b.address.longitude,
            };
            newBranches.push(branch);
        }
        if (newBranches && newBranches.length) {
            this.companyInfo = newBranches[0];
        }
    }

    setAddressData() {
        this.savedCurrencyUnit = this.detailsData.account?.currencyUnit;
        this.savedCurrencyExchangeRate = Number(this.detailsData.exchangeRate ?? 1);
        this.billingAddress = this.detailsData.account.accountAddresses.filter((d) => d.addressId == this.detailsData.billingAddressId)[0];
        this.shippingAddress = this.detailsData.account.accountAddresses.filter((d) => d.addressId == this.detailsData.shippingAddressId)[0];
        this.billingAddressContact = this.detailsData?.contact;
        this.shippingAddressContact = this.detailsData?.shipContact;
        if (this.channel == this.soChannel.ESTIMATES) {
            this.checkCountryToShowSplitView(this.billingAddress);
        } else if (this.channel == this.soChannel.SALES_ORDER) {
            this.checkCountryToShowSplitView(this.shippingAddress);
        }
    }
    checkCountryToShowSplitView(address) {
        const isInIndia = address?.address?.countryId === TaxCountry.India;
        if (isInIndia) {
            const isSameCountry = address?.address?.countryId === this.detailsData?.warehouse?.address?.countryId;
            const isSameState = address?.address?.state?.name?.toLowerCase() === this.detailsData?.warehouse?.address?.state?.name?.toLowerCase();
            this.isStateMatched = isSameCountry && isSameState;
            this.showTaxBasedSplitView = true;
        } else {
            this.showTaxBasedSplitView = false;
        }

        if (this.showTaxBasedSplitView) {
            const taxSplitObj = this.taxSplitService.prepareTaxBasedProductsList(this.purchaseListTB, this.channel, this.isStateMatched);
            this.taxBasedProductsCols = taxSplitObj?.taxBasedProductsCols;
            this.taxBasedProductsList = taxSplitObj?.taxBasedProductsList;
        }
    }

    setDocName() {
        this.docName = this.pdfHeader[this.channel];
    }
    calculateTotalAmount() {
        if (this.channel == this.soChannel.ESTIMATES) {
            this.totalAmount = this.detailsData?.estimateProducts?.reduce((total, item) => total + Number(item?.totalAmount), 0);
            // this.productsTotalAdditionalAmount = this.detailsData?.estimateProducts?.reduce((total, item) => total + Number(item?.additionalAmount), 0);
            this.productsTotalAdditionalAmount = this.calculateEstimateProductsTotalAdditionalAmount();
        } else if (this.channel == this.soChannel.SALES_ORDER) {
            this.totalAmount = this.detailsData?.salesOrderProducts?.reduce((total, item) => total + Number(item?.totalAmount), 0);
            // this.productsTotalAdditionalAmount = this.detailsData?.salesOrderProducts?.reduce((total, item) => total + Number(item?.additionalAmount), 0);
            this.productsTotalAdditionalAmount = this.calculateSalesOrderProductsTotalAdditionalAmount();
        } else if (this.channel == this.soChannel.CREDIT_MEMO) {
            this.totalAmount = this.detailsData?.creditMemoProducts?.reduce((total, item) => total + Number(item?.totalAmount), 0);
            // this.productsTotalAdditionalAmount = this.detailsData?.creditMemoProducts?.reduce((total, item) => total + Number(item?.additionalAmount), 0);
            this.productsTotalAdditionalAmount = this.calculateCreditMemoProductsTotalAdditionalAmount();
        }
    }
    calculateEstimateProductsTotalAdditionalAmount() {
        const getAdditionalAmount = (item) => {
            let total = item?.product?.typeId == 4 ? 0 : Number(item?.additionalAmount) || 0;
            if (item?.estimateBundleDetail && item.estimateBundleDetail?.length > 0) {
                total += item.estimateBundleDetail.reduce((sum, bundleItem) => {
                    return sum + getAdditionalAmount(bundleItem);
                }, 0);
            }
            return total;
        };
    
        const productsTotalAdditionalAmount = this.detailsData?.estimateProducts?.reduce((total, item) => {
            return total + getAdditionalAmount(item);
        }, 0) || 0;

        return productsTotalAdditionalAmount;
    }
    calculateSalesOrderProductsTotalAdditionalAmount() {
        const getAdditionalAmount = (item) => {
            let total = item?.product?.typeId == 4 ? 0 : Number(item?.additionalAmount) || 0;
            if (item?.salesOrderBundleDetail && item.salesOrderBundleDetail?.length > 0) {
                total += item.salesOrderBundleDetail.reduce((sum, bundleItem) => {
                    return sum + getAdditionalAmount(bundleItem);
                }, 0);
            }
            return total;
        };
    
        const productsTotalAdditionalAmount = this.detailsData?.salesOrderProducts?.reduce((total, item) => {
            return total + getAdditionalAmount(item);
        }, 0) || 0;

        return productsTotalAdditionalAmount;
    }
    calculateCreditMemoProductsTotalAdditionalAmount() {
        const getAdditionalAmount = (item) => {
            let total = item?.product?.typeId == 4 ? 0 : Number(item?.additionalAmount) || 0;
            if (item?.creditMemoBundleDetail && item.creditMemoBundleDetail?.length > 0) {
                total += item.creditMemoBundleDetail.reduce((sum, bundleItem) => {
                    return sum + getAdditionalAmount(bundleItem);
                }, 0);
            }
            return total;
        };
    
        const productsTotalAdditionalAmount = this.detailsData?.creditMemoProducts?.reduce((total, item) => {
            return total + getAdditionalAmount(item);
        }, 0) || 0;

        return productsTotalAdditionalAmount;
    }

    getBundleLength() {
        let arr: any[] = [];
        for (let product of this.purchaseListTB) {
            if (this.channel == this.soChannel.ESTIMATES) {
                for (let s of product?.estimateBundleDetail) {
                    arr.push(s);
                }
            } else if (this.channel == this.soChannel.SALES_ORDER) {
                for (let s of product?.salesOrderBundleDetail) {
                    arr.push(s);
                }
            } else if (this.channel == this.soChannel.INVOICING) {
                for (let s of product?.invoiceBundleDetail) {
                    arr.push(s);
                }
            } else if (this.channel == this.soChannel.CREDIT_MEMO) {
                for (let s of product?.creditMemoBundleDetail) {
                    arr.push(s);
                }
            }
        }
        this.bundleLists = JSON.parse(JSON.stringify(arr));
    }

    getProductSerialDetails() {
        this.productsSerialDetails = [];
        if (this.channel && this.channel == this.soChannel.SALES_ORDER && this.purchaseListTB) {
            this.productsSerialDetails = this.utilityService.getSalesOrderProductsSerialDetails(this.purchaseListTB);
        } else if (this.channel && this.channel == this.soChannel.INVOICING && this.purchaseListTB) {
            this.productsSerialDetails = this.utilityService.getInvoiceProductsSerialDetails(this.purchaseListTB);
        } else if (this.channel && this.channel == this.soChannel.CREDIT_MEMO && this.purchaseListTB) {
            this.productsSerialDetails = this.utilityService.getCreditMemoProductsSerialDetails(this.purchaseListTB);
        }
    }

    getOrganizationLogo() {
        return this.getBase64ImageFromUrl(this.app.appLogo)
            .then((response: string) => {
                let res: string = response;
                return res.replace("data:application/octet-stream", "data:image/png");
            })
            .catch((error) => {
                return null;
            });
    }

    async getBase64ImageFromUrl(imageUrl) {
        var res = await fetch(imageUrl);
        var blob = await res.blob();
        return new Promise((resolve, reject) => {
            var reader = new FileReader();
            reader.addEventListener(
                "load",
                function () {
                    resolve(reader.result);
                },
                false,
            );
            reader.onerror = () => {
                return reject(this);
            };
            reader.readAsDataURL(blob);
        });
    }

    print(type = PrintType.PRINT) {
        this.appMainService.isShowSpinner("printpdf");
        forkJoin([
            this.getOrganizationLogo(),
            this.ordersService.getSignatureByObjectName(this.objectName).pipe(
                catchError((error) => {
                    return of(null);
                }),
            ),
        ]).subscribe(([logoResponse, signatureResponse]) => {
            this.appLogoPath = logoResponse; // org logo
            this.signature = signatureResponse; // signature
            if (this.signature) {
                this.getBase64ImageFromUrl(signatureResponse?.attachmentImage?.downloadLink).then((response: string) => {
                    let res: string = response;
                    this.imagePath = res.replace("data:application/octet-stream", "data:image/png");
                });
            }
            setTimeout(() => {
                this.appMainService.isHideSpinner("printpdf");
                this.processPrint(type);
            }, 1000);
        });
    }

    processPrint(type = PrintType.PRINT) {
        let pdf = new jsPDF("p", "pt", "a4", true);
        pdf.addFileToVFS("Roboto-Regular.ttf", RobotoFont);
        pdf.addFont("Roboto-Regular.ttf", "Roboto-Regular", "normal");
        pdf.setFont("Roboto-Regular");
        pdf.setProperties({ title: "" });

        let pdfFileName = "";
        let html = document?.getElementById("soPreview").innerHTML;
        if (type == PrintType.PACKSLIPPRINT) {
            html = document?.getElementById("soPackSlipPreview").innerHTML;
            pdfFileName = this.generatedPdfFileName[PrintType.PACKSLIPPRINT] + "_" + this.detailsData?.number;
        } else if (type == PrintType.PICKSLIPPRINT) {
            html = document?.getElementById("soPickSlipPreview").innerHTML;
            pdfFileName = this.generatedPdfFileName[PrintType.PICKSLIPPRINT] + "_" + this.detailsData?.number;
        } else {
            pdfFileName = this.generatedPdfFileName[this.channel] + "_" + this.detailsData?.number;
        }

        pdf.setProperties({ title: pdfFileName + ".pdf" });
        html = html.replace(/width="100%"/, 'width="575"');
        pdf.html(html, {
            margin: [30, 10],
            callback: (generatedPdf) => {
                // generatedPdf.output("dataurlnewwindow");
                const dataUri = generatedPdf.output("datauristring");
                const newTab = window.open();
                newTab?.document?.write('<iframe width="100%" height="100%" src="' + dataUri + '" type="application/pdf"></iframe>');
                if (newTab && newTab.document) {
                    newTab.document.title = pdfFileName + ".pdf";
                }
            },
        });
    }

    downloadAsPdf(type = PrintType.PRINT) {
        this.appMainService.isShowSpinner("printpdf");
        forkJoin([
            this.getOrganizationLogo(),
            this.ordersService.getSignatureByObjectName(this.objectName).pipe(
                catchError((error) => {
                    return of(null);
                }),
            ),
        ]).subscribe(([logoResponse, signatureResponse]) => {
            this.appLogoPath = logoResponse; // org logo
            this.signature = signatureResponse; // signature
            if (this.signature) {
                this.getBase64ImageFromUrl(signatureResponse?.attachmentImage?.downloadLink).then((response: string) => {
                    let res: string = response;
                    this.imagePath = res.replace("data:application/octet-stream", "data:image/png");
                });
            }
            setTimeout(() => {
                this.appMainService.isHideSpinner("printpdf");
                this.processDownloadAsPdf(type);
            }, 1000);
        });
    }

    processDownloadAsPdf(type = PrintType.PRINT) {
        let pdf = new jsPDF("p", "pt", "a4", true);
        pdf.addFileToVFS("Roboto-Regular.ttf", RobotoFont);
        pdf.addFont("Roboto-Regular.ttf", "Roboto-Regular", "normal");
        pdf.setFont("Roboto-Regular");

        let html = document?.getElementById("soPreview").innerHTML;
        if (type == PrintType.PACKSLIPPRINT) {
            html = document?.getElementById("soPackSlipPreview").innerHTML;
        } else if (type == PrintType.PICKSLIPPRINT) {
            html = document?.getElementById("soPickSlipPreview").innerHTML;
        }
        html = html.replace(/width="100%"/, 'width="575"'); //Change the table width for fitting in pdf page
        pdf.html(html, {
            callback: function (pdf) {
                pdf.save("so.pdf");
            },
            margin: [30, 10],
        });
    }

    getPdf(type = PrintType.PRINT): Observable<any> {
        return Observable.create((observer) => {
            this.appMainService.isShowSpinner("printpdf");
            forkJoin([
                this.getOrganizationLogo(),
                this.ordersService.getSignatureByObjectName(this.objectName).pipe(
                    catchError((error) => {
                        return of(null);
                    }),
                ),
            ]).subscribe(([logoResponse, signatureResponse]) => {
                this.appLogoPath = logoResponse; // org logo
                this.signature = signatureResponse; // signature
                if (this.signature) {
                    this.getBase64ImageFromUrl(signatureResponse?.attachmentImage?.downloadLink).then((response: string) => {
                        let res: string = response;
                        this.imagePath = res.replace("data:application/octet-stream", "data:image/png");
                    });
                }
                setTimeout(() => {
                    this.appMainService.isHideSpinner("printpdf");
                    const pdf = new jsPDF("p", "pt", "a4", true);
                    pdf.addFileToVFS("Roboto-Regular.ttf", RobotoFont);
                    pdf.addFont("Roboto-Regular.ttf", "Roboto-Regular", "normal");
                    pdf.setFont("Roboto-Regular");
                    let html = document?.getElementById("soPreview").innerHTML;
                    if (type === PrintType.PACKSLIPPRINT) {
                        html = document?.getElementById("soPackSlipPreview").innerHTML;
                    } else if (type === PrintType.PICKSLIPPRINT) {
                        html = document?.getElementById("soPickSlipPreview").innerHTML;
                    }
                    html = html.replace(/width="100%"/, 'width="575"');
                    pdf.html(html, {
                        margin: [30, 10],
                        callback: (pdfOutput) => {
                            observer.next(pdfOutput);
                            observer.complete();
                        },
                    });
                }, 1000);
            });
        });
    }

    replaceCommaLine(data) {
        if (data) {
            let dataToArray = data.split(",").map((item) => item.trim());
            return dataToArray;
        }
    }
}
